import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMapMarkerAlt, FaGlobeAfrica, FaLanguage, FaUsers, FaMoneyBillWave, FaLandmark, FaPaw, FaMountain, FaUmbrellaBeach, FaHiking, FaSuitcase, FaPassport, FaSimCard, FaCreditCard, FaStar } from "react-icons/fa";
import "../css/styles.css";

const Kenya = () => {
  return (
    <div className="kenya-page">
      {/* ===== HERO ===== */}
      <section className="kenya-hero">
        <div className="kenya-hero-bg">
          <img
            src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1400/v1776344823/Kenya_ullx4e.jpg"
            alt="Kenya Safari Landscape"
          />
          <div className="kenya-hero-overlay" />
        </div>
        <div className="kenya-hero-content">
          <span className="kenya-hero-eyebrow">Discover East Africa</span>
          <h1>Kenya</h1>
          <p className="kenya-hero-sub">
            Where dramatic landscapes, extraordinary wildlife, vibrant cultures, and pristine Indian Ocean beaches come together in one unforgettable journey.
          </p>
          <div className="kenya-hero-buttons">
            <Link to="/contact" className="hero-cta primary">
              Design Your Trip <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/blogs" className="hero-cta secondary">
              Continue Exploring
            </Link>
          </div>
        </div>
      </section>

      {/* ===== INTRODUCTION ===== */}
      <section className="kenya-intro">
        <div className="kenya-section-header">
          <span className="section-tag">Introduction</span>
          <h2>The Classic African Safari</h2>
          <div className="kenya-section-line" />
        </div>
        <div className="kenya-intro-text">
          <p>
            From the vast golden plains of the Masai Mara to the elephant-filled landscapes of Amboseli and the white-sand beaches along the coast, Kenya offers an incredible diversity of experiences within a single country.
          </p>
          <p>
            For many travelers, Kenya represents the classic African safari — open savannahs dotted with acacia trees, lions resting in the grass, and herds of elephants moving slowly across the horizon beneath wide African skies.
          </p>
          <p>
            But Kenya offers far more than wildlife alone. Here you can encounter rich cultural traditions, explore private wildlife conservancies, witness the Great Migration, and finish your journey relaxing on the tropical shores of the Indian Ocean.
          </p>
        </div>
      </section>

      {/* ===== GENERAL INFORMATION ===== */}
      <section className="kenya-info">
        <div className="kenya-section-header">
          <span className="section-tag centered">General Information</span>
          <h2>About Kenya</h2>
          <div className="kenya-section-line" />
        </div>
        <div className="kenya-info-grid">
          <div className="kenya-info-card">
            <div className="kenya-info-icon"><FaMapMarkerAlt /></div>
            <h3>Location</h3>
            <p>Located in East Africa along the Indian Ocean coastline, bordering Tanzania, Uganda, South Sudan, Ethiopia, and Somalia. Known for incredible geographical diversity — savannah plains, volcanic mountains, lush forests, lakes, deserts, and tropical beaches.</p>
          </div>
          <div className="kenya-info-card">
            <div className="kenya-info-icon"><FaLandmark /></div>
            <h3>History</h3>
            <p>Shaped by centuries of migration, trade, and cultural exchange. Swahili trading towns once connected Africa with Arabia, Persia, and India. Kenya gained independence from Britain in 1963 and today combines modern development with deep-rooted traditions.</p>
          </div>
          <div className="kenya-info-card">
            <div className="kenya-info-icon"><FaLanguage /></div>
            <h3>Languages</h3>
            <p>Two official languages: English and Swahili. English is widely used in business and tourism, while Swahili serves as the national language across East Africa. Many Kenyans also speak their own ethnic languages.</p>
          </div>
          <div className="kenya-info-card">
            <div className="kenya-info-icon"><FaUsers /></div>
            <h3>Population</h3>
            <p>Over 55 million people and home to more than 40 different ethnic communities, each contributing unique traditions, languages, and customs.</p>
          </div>
          <div className="kenya-info-card">
            <div className="kenya-info-icon"><FaMoneyBillWave /></div>
            <h3>Currency</h3>
            <p>Kenyan Shilling (KES). Major hotels, safari lodges, and restaurants accept credit cards and US dollars. Local currency is useful for smaller purchases and markets.</p>
          </div>
          <div className="kenya-info-card">
            <div className="kenya-info-icon"><FaGlobeAfrica /></div>
            <h3>Religion</h3>
            <p>Religiously diverse — Christianity is widely practiced, followed by Islam (particularly along the coast). Traditional beliefs and cultural practices also remain important within many communities.</p>
          </div>
        </div>
      </section>

      {/* ===== UNIQUE FACTS ===== */}
      <section className="kenya-facts">
        <div className="kenya-section-header">
          <span className="section-tag">Did You Know?</span>
          <h2>Unique Facts About Kenya</h2>
          <div className="kenya-section-line" />
        </div>
        <div className="kenya-facts-list">
          <div className="kenya-fact-item">
            <FaStar className="kenya-fact-icon" />
            <p>Home to the Great Migration — one of the world's most spectacular wildlife events.</p>
          </div>
          <div className="kenya-fact-item">
            <FaStar className="kenya-fact-icon" />
            <p>Contains landscapes ranging from snow-capped mountains to coral reefs.</p>
          </div>
          <div className="kenya-fact-item">
            <FaStar className="kenya-fact-icon" />
            <p>Considered the birthplace of the modern safari.</p>
          </div>
          <div className="kenya-fact-item">
            <FaStar className="kenya-fact-icon" />
            <p>The Great Rift Valley, one of Earth's most dramatic geological features, runs through the country.</p>
          </div>
          <div className="kenya-fact-item">
            <FaStar className="kenya-fact-icon" />
            <p>Famous worldwide for producing some of the greatest long-distance runners in athletics history.</p>
          </div>
        </div>
      </section>

      {/* ===== WHY TRAVEL TO KENYA ===== */}
      <section className="kenya-why">
        <div className="kenya-why-bg" />
        <div className="kenya-why-content">
          <div className="kenya-section-header">
            <span className="section-tag">Why Kenya?</span>
            <h2>Why Travel to Kenya</h2>
            <div className="kenya-section-line" />
          </div>
          <p>
            Kenya offers one of the most complete travel experiences in Africa. Few destinations combine extraordinary wildlife, rich cultural heritage, dramatic landscapes, and idyllic beaches in such a seamless way.
          </p>
          <p>
            Travelers can watch lions and cheetahs hunt across the plains of the Masai Mara, observe elephants roaming beneath Mount Kilimanjaro in Amboseli, explore remote conservancies in Laikipia, and later unwind on the palm-lined beaches of the Indian Ocean.
          </p>
          <p>
            Kenya also offers some of Africa's most diverse safari experiences — from luxury lodges and private conservancies to walking safaris and cultural encounters with local communities.
          </p>
          <p>
            Whether you are planning a honeymoon, a family adventure, or a once-in-a-lifetime wildlife journey, Kenya promises unforgettable moments and stories that will stay with you long after the journey ends.
          </p>
        </div>
      </section>

      {/* ===== PEOPLE & CULTURE ===== */}
      <section className="kenya-culture">
        <div className="kenya-section-header">
          <span className="section-tag centered">Heritage</span>
          <h2>People &amp; Culture</h2>
          <div className="kenya-section-line" />
        </div>
        <div className="kenya-culture-content">
          <p>
            Kenya's cultural diversity is one of its greatest strengths. With more than 40 ethnic communities, the country offers a rich mosaic of traditions, languages, and lifestyles.
          </p>
          <p>
            Among the most well-known communities are the Maasai, whose vibrant red clothing and traditional beadwork have become iconic symbols of East Africa. The Samburu people in northern Kenya maintain strong cultural traditions and a deep connection to the land, while along the coast, Swahili culture reflects centuries of trade with the Middle East and Asia.
          </p>
          <p>
            Music, storytelling, dance, and craftsmanship play an important role in everyday life. Visitors may encounter colorful markets, traditional ceremonies, and welcoming communities that are proud to share their culture and heritage.
          </p>
          <p className="kenya-karibu">
            Kenyan hospitality is warm and genuine, and visitors are often greeted with the Swahili phrase <strong>"Karibu Kenya"</strong> — Welcome to Kenya.
          </p>
        </div>
      </section>

      {/* ===== EXPERIENCES ===== */}
      <section className="kenya-experiences">
        <div className="kenya-section-header">
          <span className="section-tag centered">Experiences</span>
          <h2>Experiences in Kenya</h2>
          <p className="kenya-section-sub">A wide range of travel experiences tailored to every travel style.</p>
          <div className="kenya-section-line" />
        </div>
        <div className="kenya-exp-grid">
          <div className="kenya-exp-card">
            <div className="kenya-exp-icon"><FaPaw /></div>
            <h3>Wildlife Safaris</h3>
            <p>One of the world's premier safari destinations, offering exceptional wildlife viewing across a variety of landscapes. Encounter lions, elephants, cheetahs, giraffes, zebras, and countless other species in their natural habitats.</p>
          </div>
          <div className="kenya-exp-card">
            <div className="kenya-exp-icon"><FaGlobeAfrica /></div>
            <h3>Cultural Experiences</h3>
            <p>Meaningful cultural encounters beyond wildlife. Visit local communities, learn about traditional lifestyles, and gain insight into the diverse cultures that shape the country.</p>
          </div>
          <div className="kenya-exp-card">
            <div className="kenya-exp-icon"><FaHiking /></div>
            <h3>Adventure Travel</h3>
            <p>Walking safaris, mountain trekking, camel safaris, and exploring the dramatic landscapes of the Great Rift Valley — ideal for travelers seeking adventure.</p>
          </div>
          <div className="kenya-exp-card">
            <div className="kenya-exp-icon"><FaUmbrellaBeach /></div>
            <h3>Beach Escapes</h3>
            <p>Beautiful white-sand beaches, turquoise waters, and vibrant marine life along the Indian Ocean. Combine your safari with a relaxing beach stay to complete the experience.</p>
          </div>
        </div>
      </section>

      {/* ===== SAFARI DESTINATIONS ===== */}
      <section className="kenya-destinations">
        <div className="kenya-section-header">
          <span className="section-tag centered">Safari Destinations</span>
          <h2>Where to Go on Safari</h2>
          <div className="kenya-section-line" />
        </div>
        <div className="kenya-dest-grid">
          <div className="kenya-dest-card">
            <div className="kenya-dest-card-header">
              <FaMapMarkerAlt />
              <h3>Masai Mara</h3>
            </div>
            <p>One of Africa's most famous wildlife reserves, renowned for its incredible concentration of wildlife and the annual Great Migration, when millions of wildebeest and zebras cross the plains in search of fresh grazing.</p>
          </div>
          <div className="kenya-dest-card">
            <div className="kenya-dest-card-header">
              <FaMapMarkerAlt />
              <h3>Amboseli</h3>
            </div>
            <p>Famous for its large elephant herds and spectacular views of Mount Kilimanjaro, Africa's highest mountain.</p>
          </div>
          <div className="kenya-dest-card">
            <div className="kenya-dest-card-header">
              <FaMapMarkerAlt />
              <h3>Laikipia</h3>
            </div>
            <p>A vast conservation region known for its private conservancies, exclusive lodges, and diverse wildlife. Offers unique experiences including walking safaris and night game drives.</p>
          </div>
          <div className="kenya-dest-card">
            <div className="kenya-dest-card-header">
              <FaMapMarkerAlt />
              <h3>Samburu</h3>
            </div>
            <p>Located in northern Kenya, known for its rugged landscapes and rare wildlife species such as Grevy's zebra, reticulated giraffe, and the Somali ostrich.</p>
          </div>
          <div className="kenya-dest-card">
            <div className="kenya-dest-card-header">
              <FaMapMarkerAlt />
              <h3>Tsavo</h3>
            </div>
            <p>Tsavo East and Tsavo West together form one of the largest protected ecosystems in Africa. Famous for dramatic landscapes and the distinctive red-dust elephants.</p>
          </div>
        </div>
      </section>

      {/* ===== COASTAL DESTINATIONS ===== */}
      <section className="kenya-coastal">
        <div className="kenya-section-header">
          <span className="section-tag centered">Coastal Destinations</span>
          <h2>Kenya's Indian Ocean Coast</h2>
          <div className="kenya-section-line" />
        </div>
        <div className="kenya-dest-grid">
          <div className="kenya-dest-card kenya-dest-card-coastal">
            <div className="kenya-dest-card-header">
              <FaUmbrellaBeach />
              <h3>Diani</h3>
            </div>
            <p>Kenya's most famous coastal destination, known for its powder-white sand, clear turquoise waters, and excellent resorts.</p>
          </div>
          <div className="kenya-dest-card kenya-dest-card-coastal">
            <div className="kenya-dest-card-header">
              <FaUmbrellaBeach />
              <h3>Watamu</h3>
            </div>
            <p>A relaxed coastal atmosphere famous for its marine park, coral reefs, and exceptional snorkeling and diving.</p>
          </div>
          <div className="kenya-dest-card kenya-dest-card-coastal">
            <div className="kenya-dest-card-header">
              <FaUmbrellaBeach />
              <h3>Malindi</h3>
            </div>
            <p>Blends Swahili culture with Italian influences and offers beautiful beaches, historic architecture, and vibrant coastal life.</p>
          </div>
          <div className="kenya-dest-card kenya-dest-card-coastal">
            <div className="kenya-dest-card-header">
              <FaUmbrellaBeach />
              <h3>Mombasa</h3>
            </div>
            <p>Kenya's historic port city, rich in culture and history, with centuries-old Swahili architecture and bustling markets.</p>
          </div>
        </div>
      </section>

      {/* ===== HOW SAFARI WORKS ===== */}
      <section className="kenya-how">
        <div className="kenya-section-header">
          <span className="section-tag centered">Plan With Confidence</span>
          <h2>How a Kenya Safari Works</h2>
          <div className="kenya-section-line" />
        </div>
        <div className="kenya-how-grid">
          <div className="kenya-how-card">
            <div className="kenya-how-number">01</div>
            <h3>Safari Lodges &amp; Camps</h3>
            <p>Accommodations are located within or near wildlife areas, ranging from luxury lodges to intimate tented camps. Many operate on an all-inclusive basis with meals, game drives, and guided activities.</p>
          </div>
          <div className="kenya-how-card">
            <div className="kenya-how-number">02</div>
            <h3>Game Drives</h3>
            <p>The highlight of most safaris. Travel in specially designed 4x4 vehicles with experienced guides. Game drives usually take place early morning and late afternoon when wildlife is most active.</p>
          </div>
          <div className="kenya-how-card">
            <div className="kenya-how-number">03</div>
            <h3>Private Conservancies</h3>
            <p>Exclusive safari experiences with fewer vehicles and more flexibility. Activities include walking safaris, night game drives, off-road wildlife viewing, and cultural visits.</p>
          </div>
          <div className="kenya-how-card">
            <div className="kenya-how-number">04</div>
            <h3>Getting Around</h3>
            <p>Travel between destinations by light aircraft or road transfers. Small aircraft connect Nairobi with major parks and conservancies, providing fast and scenic travel.</p>
          </div>
          <div className="kenya-how-card">
            <div className="kenya-how-number">05</div>
            <h3>Safari &amp; Beach Combo</h3>
            <p>One of Kenya's greatest advantages — combine a wildlife safari with a beach holiday. After exploring the savannah, continue to the Indian Ocean coast to relax on tropical beaches.</p>
          </div>
        </div>
      </section>

      {/* ===== PRACTICAL GUIDE ===== */}
      <section className="kenya-practical">
        <div className="kenya-section-header">
          <span className="section-tag centered">Before You Go</span>
          <h2>Practical Travel Guide</h2>
          <div className="kenya-section-line" />
        </div>
        <div className="kenya-practical-grid">
          <div className="kenya-practical-card">
            <div className="kenya-practical-icon"><FaPassport /></div>
            <h3>Entry Requirements</h3>
            <p>Most travelers require an Electronic Travel Authorization (eTA) before entering Kenya. Passports should be valid for at least six months beyond the date of travel.</p>
          </div>
          <div className="kenya-practical-card">
            <div className="kenya-practical-icon"><FaSuitcase /></div>
            <h3>What to Pack</h3>
            <ul>
              <li>Lightweight clothing in neutral colors</li>
              <li>Light jacket for cool mornings and evenings</li>
              <li>Comfortable walking shoes</li>
              <li>Sun protection (hat, sunglasses, sunscreen)</li>
              <li>Camera or binoculars for wildlife viewing</li>
            </ul>
          </div>
          <div className="kenya-practical-card">
            <div className="kenya-practical-icon"><FaSimCard /></div>
            <h3>SIM Cards &amp; Connectivity</h3>
            <p>Local SIM cards are available at airports and major cities. Mobile coverage is generally good in towns and most safari areas.</p>
          </div>
          <div className="kenya-practical-card">
            <div className="kenya-practical-icon"><FaCreditCard /></div>
            <h3>Currency &amp; Payments</h3>
            <p>Credit cards are widely accepted at hotels and lodges. Carry some Kenyan shillings for tips, markets, and smaller purchases. M-Pesa mobile payments are widely used throughout the country.</p>
          </div>
        </div>
      </section>

      {/* ===== CTA BUTTONS ===== */}
      <section className="kenya-cta">
        <div className="kenya-cta-content">
          <h2>Ready to Experience Kenya?</h2>
          <p>Let us craft your perfect Kenyan adventure — from wildlife safaris to beach escapes.</p>
          <div className="kenya-cta-buttons">
            <Link to="/blogs" className="hero-cta secondary">
              Continue Exploring <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/contact" className="hero-cta primary">
              Design Your Trip <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Kenya;
